import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaUserCog } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import { filterMenuModuleRecords } from './outletMenuModuleRegistry';

function KaryawanModules() {
    const [modules, setModules] = useState([]);
    const [moduleMap, setModuleMap] = useState({});
    const [karyawanMap, setKaryawanMap] = useState({});
    const [filteredModules, setFilteredModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) navigate('/loginadmin');
        else {
            fetchKaryawanList(token);
            fetchModuleList(token);
        }
    }, [navigate]);

    const fetchKaryawanList = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}karyawanlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === "0" && data.results) {
                const map = data.results.reduce((acc, karyawan) => {
                    acc[karyawan.id] = `${karyawan.nama} (${karyawan.OutletName})`;
                    return acc;
                }, {});
                setKaryawanMap(map);
            }
        } catch (err) {
            console.error('Failed to fetch karyawan list:', err);
        }
    };

    const fetchModuleList = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === "0" && data.results) {
                const map = filterMenuModuleRecords(data.results).reduce((acc, module) => {
                    acc[module.ID] = module.Modulename;
                    return acc;
                }, {});
                setModuleMap(map);
            }
        } catch (err) {
            console.error('Failed to fetch module list:', err);
        }
    };

    const fetchModules = async (token) => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}karyawanmodules`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const data = await response.json();
            if (data.error === 0 && data.data) {
                const transformedData = data.data.map(item => ({
                    ID: item.id,
                    KaryawanName: karyawanMap[item.id_karyawan] || `ID: ${item.id_karyawan}`,
                    IDKaryawan:item.id_karyawan,
                    Modules: item.modules.map(moduleId => moduleMap[moduleId] || `ID: ${moduleId}`).join(', '),
                    CreatedAt: item.created_at,
                    UpdatedAt: item.updated_at
                }));
                setModules(transformedData);
                setFilteredModules(transformedData);
            }
        } catch (err) {
            console.error('Failed to fetch karyawan modules:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (token && Object.keys(karyawanMap).length > 0 && Object.keys(moduleMap).length > 0) {
            fetchModules(token);
        }
    }, [karyawanMap, moduleMap]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        const filtered = modules.filter(module =>
            module.KaryawanName.toLowerCase().includes(value.toLowerCase()) ||
            module.Modules.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredModules(filtered);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('sessionToken');
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}deletemoduleskaryawan`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ id })
                    });

                    const data = await response.json();
                    if (data.error === 0) {
                        Swal.fire('Deleted!', 'Module has been deleted.', 'success');
                        fetchModules(token);
                    } else {
                        Swal.fire('Error!', 'Failed to delete module.', 'error');
                    }
                } catch (err) {
                    console.error(err);
                    Swal.fire('Error!', 'Failed to delete module.', 'error');
                }
            }
        });
    };

    const columns = [
        { name: 'ID', selector: row => row.ID, sortable: true, width: '80px' },
        { name: 'Karyawan Name', selector: row => row.KaryawanName, sortable: true, grow: 2 },
        { name: 'Modules', selector: row => row.Modules, sortable: true, grow: 2 },
        { name: 'Created At', selector: row => row.CreatedAt, sortable: true, grow: 2 },
        { name: 'Updated At', selector: row => row.UpdatedAt, sortable: true, grow: 2 },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <Button variant="outline-warning" size="sm" onClick={() => navigate(`/edit-karyawan-module/${row.IDKaryawan}`)}>
                        <FaEdit /> Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.ID)}>
                        <FaTrash /> Delete
                    </Button>
                </div>
            ),
            width: '200px'
        }
    ];

    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaUserCog}
                    eyebrow="Access administration"
                    title="Employee Module Assignment"
                    description="Kelola penetapan modul untuk setiap karyawan agar hak akses operasional sesuai dengan peran dan tanggung jawabnya."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Employee Module Assignment' },
                    ]}
                    actions={(
                        <Button variant="primary" onClick={() => navigate('/add-karyawan-module')}>
                            <FaPlus /> Assign Module
                        </Button>
                    )}
                    meta={(
                        <span className="soft-stat">
                            {filteredModules.length} assignments
                        </span>
                    )}
                />

                <div className="section-card table-card">
                    <div className="table-toolbar">
                        <span className="status-chip">{filteredModules.length} assignments</span>
                        <div className="control-with-icon search-control">
                            <span className="control-with-icon__icon">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search by employee or module"
                                value={search}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>

                    <div className="card-body pt-0">
                        <AdminDataTable
                            columns={columns}
                            data={filteredModules}
                            pagination
                            progressPending={loading}
                            highlightOnHover
                            pointerOnHover
                            responsive
                            striped
                            emptyTitle="No employee module assignments found"
                            emptyDescription="Tambahkan penetapan modul karyawan untuk mengatur akses kerja harian."
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
}

export default KaryawanModules;
